import React, { useState, useEffect } from "react"
import { bindActionCreators } from "redux"
import { connect } from "react-redux"
import _ from "lodash"
import moment from "moment"
import Title from "../general/title"
import { Bar } from "react-chartjs-2"
import lineOptions from "helpers/lineOptions"
import colorArray from "helpers/colorArray"
import ImageExport from "components/ImageExport"
import FullTable from "components/FullTable"
import classnames from "classnames"
import ExcelOnly from "components/ExcelOnly"
import Switch from "react-switch"
import helpers from "helpers/helpers"

function YearOverYear(props) {
	const [excel, setExcel] = useState([])
	const [buckets, setBuckets] = useState({})
	const [showFilters, setShowFilters] = useState(false)
	const [activeYears, setActiveYears] = useState([moment().year()])
	const [allYears, setAllYears] = useState([])
	const [seniorActive, setSeniorActive] = useState(false)

	useEffect(() => {
		determineYears()
	}, [])

	useEffect(() => {
		determineYears()
	}, [props.payloadStatus])

	useEffect(() => {
		determineYears()
	}, [props.activeProperties])

	useEffect(() => {
		chartData()
		chartDataISD()
	}, [activeYears, seniorActive, allYears])

	const determineYears = () => {
		let years = []
		_.forEach(props.payload.exec.sales, (v) => {
			let year = moment.unix(v.sale_date).year()
			if (!years.includes(year)) {
				years.push(year)
			}
		})
		years.sort()
		years = _.reverse(years)
		setAllYears(years)
	}

	const chartData = () => {
		let data = {}
		let dayOfYear = moment().dayOfYear()
		let property = props.activeProperties[0]
		let sections = _.filter(props.payload.public.sections, { property })
		let ret = {}
		let excel = []
		// calculate gross sales
		_.forEach(props.payload.exec.sales, (v) => {
			if (v.property !== property) {
				return
			}
			let saleYear = moment.unix(v.sale_date).year()
			if (activeYears.includes(saleYear)) {
				let builder = _.find(props.payload.public.builders, { id: v.builder })
				let section = _.find(sections, { id: v.section_id })
				// Ensure previous years end at this point in current years
				let doy = moment.unix(v.sale_date).dayOfYear()
				if (doy > dayOfYear) {
					return
				}
				if (v.size && builder) {
					let builderName = builder.name.replace(/[^\w- ]+/g, "")
					let bucket
					if (seniorActive && section && section.senior) {
						bucket = "Active Adult"
					} else {
						bucket = `${v.size} - ${builderName}`
						if (section && section.senior) {
							bucket = `AA ${bucket}`
						}
					}
					if (!_.has(ret, `${bucket}`)) {
						// Check if this exists in state already and check the display status
						let displayStatus
						if (_.has(buckets, `${property}.${bucket}`)) {
							displayStatus = buckets[property][bucket].display
						}

						let y = {
							display: displayStatus === false ? false : true,
							data: {},
							lot_size: v.size,
							builder: builderName,
						}
						for (let year of activeYears) {
							y["data"][year] = 0
						}
						_.set(ret, `${bucket}`, y)
					}
					ret[bucket]["data"][saleYear]++
				}
			}
		})
		_.forEach(props.payload.exec.busts, (v) => {
			if (v.property !== property) {
				return
			}
			let saleYear = parseInt(moment.unix(v.bustout_date).format("Y"))
			if (activeYears.includes(saleYear)) {
				let section = _.find(sections, { id: v.section_id })
				let builder = _.find(props.payload.public.builders, {
					id: v.builder,
				})
				// Ensure previous years end at this point in current years
				let doy = moment.unix(v.bustout_date).dayOfYear()
				if (doy > dayOfYear) {
					return
				}
				if (v.size && builder) {
					let builderName = builder.name.replace(/[^\w- ]+/g, "")
					let bucket
					if (seniorActive && section && section.senior) {
						bucket = "Active Adult"
					} else {
						bucket = `${v.size} - ${builderName}`
						if (section && section.senior) {
							bucket = `AA ${bucket}`
						}
					}
					if (!_.has(ret, `${bucket}`)) {
						// Check if this exists in state already and check the display status
						let displayStatus
						if (_.has(buckets, `${property}.${bucket}`)) {
							displayStatus = buckets[property][bucket].display
						}
						let y = {
							display: displayStatus === false ? false : true,
							data: {},
						}
						for (let year of activeYears) {
							y["data"][year] = 0
						}
						_.set(ret, `${bucket}`, y)
					}

					ret[bucket]["data"][saleYear]--
				}
			}
		})
		let keys = _.keys(ret)
		// This is needed due to 100' showing up before 28', for example.
		keys = _.sortBy(keys, (v) => {
			let num = v.replace(/[^0-9]/g, "")
			num = parseInt(num)
			return num
		})
		let sorted = {}
		_.forEach(keys, (v) => {
			sorted[v] = ret[v]
		})
		//console.log(sorted)
		data[property] = sorted
		_.forEach(data, (row, propertyID) => {
			let property = _.find(props.payload.public.properties, { id: parseInt(propertyID) })
			_.forEach(row, (d) => {
				let store = []
				store.push(["Builder", `${d.builder} - ${d.lot_size}`])
				_.forEach(d.data, (v, year) => {
					store.push([`Year-${year}`, v])
				})
				let map = new Map(store)
				store = Object.fromEntries(map)
				excel.push(store)
			})
		})
		//console.log(data)
		//console.log(excel)

		setBuckets(data)
		setExcel(excel)
	}

	const chartDataISD = () => {
		let dayOfYear = moment().dayOfYear()
		let property = parseInt(props.activeProperties[0])
		let sections = _.filter(props.payload.public.sections, { property })
		// Last 3 years
		let isds = []
		let store = {}
		let setNames = []

		// all isds
		_.forEach(props.payload.exec.sales, (v) => {
			let school = _.find(props.payload.public.isds, { id: v.isd })
			if (!school) {
				return
			}
			isds.push(school.name)
		})
		_.forEach(props.payload.exec.bustouts, (v) => {
			let school = _.find(props.payload.public.isds, { id: v.isd })
			if (!school) {
				return
			}
			isds.push(school.name)
		})
		isds = _.uniq(isds)
		_.forEach(activeYears, (year) => {
			_.forEach(isds, (isd) => {
				setNames.push(`${year} - ${isd}`)
			})
		})
		// Create the shell
		_.forEach(props.payload.exec.sales, (v) => {
			if (v.property !== property) {
				return
			}
			let saleYear = moment.unix(v.sale_date).year()
			let school = _.find(props.payload.public.isds, { id: v.isd })
			if (!school) {
				return
			}
			if (activeYears.includes(saleYear)) {
				let builder = _.find(props.payload.public.builders, { id: v.builder })
				let section = _.find(sections, { id: v.section_id })
				// Ensure previous years end at this point in current years
				let doy = moment.unix(v.sale_date).dayOfYear()
				if (doy > dayOfYear) {
					return
				}
				if (v.size && builder) {
					let builderName = builder.name.replace(/[^\w- ]+/g, "")
					let bucket
					if (seniorActive && section && section.senior) {
						bucket = "Active Adult"
					} else {
						bucket = `${v.size} - ${builderName}`
						if (section && section.senior) {
							bucket = `AA ${bucket}`
						}
					}
					let yearSchool = `${saleYear} - ${school.name}`
					if (!_.has(store, `${bucket}`)) {
						for (let year of activeYears) {
							_.forEach(isds, (isd) => {
								let internal = `${year} - ${isd}`
								_.set(store, `${bucket}.${internal}`, 0)
							})
						}
					}
					store[bucket][yearSchool]++
				}
			}
		})
		_.forEach(props.payload.exec.busts, (v) => {
			if (v.property !== property) {
				return
			}
			let saleYear = moment.unix(v.bustout_date).year()
			let section = _.find(sections, { id: v.section_id })
			let school = _.find(props.payload.public.isds, { id: v.isd })
			if (!school) {
				return
			}
			if (activeYears.includes(saleYear)) {
				let builder = _.find(props.payload.public.builders, { id: v.builder })
				// Ensure previous years end at this point in current years
				let doy = moment.unix(v.bustout_date).dayOfYear()
				if (doy > dayOfYear) {
					return
				}
				if (v.size && builder) {
					let builderName = builder.name.replace(/[^\w- ]+/g, "")
					let bucket
					if (seniorActive && section && section.senior) {
						bucket = "Active Adult"
					} else {
						bucket = `${v.size} - ${builderName}`
						if (section && section.senior) {
							bucket = `AA ${bucket}`
						}
					}
					let yearSchool = `${saleYear} - ${school.name}`
					if (!_.has(store, `${bucket}`)) {
						for (let year of activeYears) {
							_.forEach(isds, (isd) => {
								let internal = `${year} - ${isd}`
								_.set(store, `${bucket}.${internal}`, 0)
							})
						}
					}
					store[bucket][yearSchool]--
				}
			}
		})
		// Tack on 0's
		_.forEach(store, (v, k) => {
			_.forEach(setNames, (label) => {
				if (!_.has(v, label)) {
					v[label] = 0
				}
			})
		})
		// Sort the lot types by size
		let keys = _.keys(store)
		// This is needed due to 100' showing up before 28', for example.
		keys = _.sortBy(keys, (v) => {
			let num = v.replace(/[^0-9]/g, "")
			num = parseInt(num)
			return num
		})
		let sorted = {}
		_.forEach(keys, (v) => {
			sorted[v] = store[v]
		})
		let sortedStore = []
		_.forEach(sorted, (v, k) => {
			sortedStore.push({
				name: k,
				values: v,
			})
		})
		//sortedStore = _.sortBy(sortedStore, "name") // This will group AA, if we ever want that.
		let labels = []
		_.forEach(sortedStore, (v) => {
			labels.push(v.name)
		})
		let datasets = []
		_.forEach(setNames, (v, k) => {
			datasets.push({
				label: v,
				backgroundColor: colorArray[k],
				//borderColor: "#3295EC",
				borderWidth: 1,
				//hoverBackgroundColor: "rgba(50,149,236,0.5)",
				//hoverBorderColor: "rgba(50,149,236,0.9)",
				data: [],
				stack: v.substring(0, 4),
			})
		})
		_.forEach(sortedStore, (v) => {
			_.forEach(v.values, (num, label) => {
				let i = _.findIndex(datasets, { label: label })
				datasets[i].data.push(num)
			})
		})
		let final = {
			labels,
			datasets,
		}
		return final
	}

	const chartDataMonth = () => {
		//? Raw store doesn't care about active years, used in the table.
		let property = parseInt(props.activeProperties[0])
		// Last 3 years
		let months = [
			"January",
			"February",
			"March",
			"April",
			"May",
			"June",
			"July",
			"August",
			"September",
			"October",
			"November",
			"December",
		]
		let store = {}
		let rawStore = {}
		const rawInput = (year, month, direction) => {
			if (!_.has(rawStore, `${year}.${month}`)) {
				_.setWith(rawStore, `${year}.${month}`, 0, Object)
			}
			if (direction === "positive") {
				rawStore[year][month]++
			} else {
				rawStore[year][month]--
			}
		}
		_.forEach(activeYears, (v) => {
			_.forEach(months, (m) => {
				_.setWith(store, `${v}.${m}`, 0, Object)
			})
		})
		_.forEach(props.payload.exec.sales, (v) => {
			if (v.property !== property) {
				return
			}
			let saleYear = moment.unix(v.sale_date).year()
			let saleMonth = moment.unix(v.sale_date).format("MMMM")
			if (activeYears.includes(saleYear)) {
				store[saleYear][saleMonth]++
			}
			rawInput(saleYear, saleMonth, "positive")
		})
		_.forEach(props.payload.exec.busts, (v) => {
			if (v.property !== property) {
				return
			}
			let saleYear = moment.unix(v.bustout_date).year()
			let saleMonth = moment.unix(v.bustout_date).format("MMMM")
			if (activeYears.includes(saleYear)) {
				store[saleYear][saleMonth]--
			}
			rawInput(saleYear, saleMonth, "negative")
		})
		let i = 0
		let datasets = _.map(activeYears, (v) => {
			i++
			let arr = _.map(months, (m) => {
				return store[v][m]
			})
			return {
				label: v,
				backgroundColor: colorArray[i],
				borderColor: colorArray[i],
				borderWidth: 1,
				hoverBackgroundColor: "rgba(50,149,236,0.5)",
				hoverBorderColor: "rgba(50,149,236,0.9)",
				data: arr,
			}
		})
		//? Now go through rawStore and ensure all months are present on all years.
		_.forEach(rawStore, (v, year) => {
			_.forEach(months, (m) => {
				if (!_.has(v, m)) {
					rawStore[year][m] = 0
				}
			})
			rawStore[year].total = _.sum(_.values(rawStore[year]))
		})
		return {
			labels: months,
			datasets,
			store,
			rawStore,
		}
	}

	const filterChange = (i, property) => {
		let bucks = _.cloneDeep(buckets)
		bucks[property][i].display = !bucks[property][i].display
		setBuckets(bucks)
	}

	const renderFilters = (property) => {
		let checkboxes = []
		_.forEach(buckets[property], (v, k) => {
			//let index = _.findIndex(buckets[property], { Header: v.Header })
			checkboxes.push(
				<li
					className="filterCheckboxes__item"
					key={`${property}-${k}`}
					data-value={k}
					onClick={() => {
						filterChange(k, property)
					}}>
					<input
						type="checkbox"
						name={k}
						value={k}
						checked={buckets[property][k].display}
						onChange={() => {}}
					/>
					{k}
				</li>
			)
		})

		return checkboxes
	}

	const yearAction = (v) => {
		let years = _.xor(activeYears, [v])
		setActiveYears(years)
		chartData()
	}

	const generateCharts = () => {
		let property = props.activeProperties[0]
		let pInfo = _.find(props.payload.public.properties, { id: property })
		let values = buckets[property]
		//console.log(values)
		let d = {}
		let labels = []
		_.forEach(activeYears, (year) => {
			year = year.toString()
			d[year] = []
			_.forEach(values, (builderData) => {
				if (builderData.display === false) {
					return
				}
				d[year].push(builderData.data[year])
			})
		})
		// Generate labels
		_.forEach(values, (v, k) => {
			if (v.display) {
				labels.push(k)
			}
		})
		//console.log(d, labels)
		let i = 0
		let datasets = _.map(activeYears, (v) => {
			i++
			return {
				label: v,
				backgroundColor: colorArray[i],
				borderColor: colorArray[i],
				borderWidth: 1,
				hoverBackgroundColor: "rgba(50,149,236,0.5)",
				hoverBorderColor: "rgba(50,149,236,0.9)",
				data: d[v],
			}
		})
		const data = {
			labels: labels,
			datasets,
		}

		const opts = {
			legend: {
				display: true,
				position: "bottom",
			},
		}

		let toggle = () => {
			setShowFilters(!showFilters)
		}
		lineOptions.scales.yAxes = [
			{
				ticks: {
					beginAtZero: true,
				},
			},
		]
		return (
			<div key={property} style={{ marginBottom: 70 }}>
				<div className="yearList">
					<ul className="yearList__list">
						{_.map(allYears, (v) => {
							return (
								<li
									key={v}
									className={classnames("yearList__item", {
										active: activeYears.includes(v),
									})}
									onClick={() => yearAction(v)}>
									{v}
								</li>
							)
						})}
					</ul>
				</div>
				<div>
					<div className="inlined right">
						<ImageExport name="YoY" block={true} twoUp={true} />
						<ExcelOnly data={excel} index={0} title="Year Over Year" />
					</div>
					<ul className="totalBoxes">
						{_.map(d, (v, k) => {
							return (
								<li key={k}>
									<span>{k}</span>
									<span>{_.sum(v)}</span>
								</li>
							)
						})}
					</ul>
					<div className="titleFilter">
						<h3>{pInfo.name}</h3>
					</div>
					<div className="filterCheckboxes__cont">
						<p className="filterCheckboxes__toggle" onClick={() => toggle()} data-html2canvas-ignore="true">
							{showFilters ? "Hide" : "Show"} Filters
						</p>
						{showFilters && (
							<ul className="filterCheckboxes" data-html2canvas-ignore="true">
								{renderFilters(property)}
							</ul>
						)}
					</div>

					<Bar data={data} options={lineOptions} key={property} />
				</div>
				<div>
					<ImageExport name="YoYSchool" />
					<h3>By Month</h3>
					{generateChartsMonth()}
				</div>
				<div>
					<ImageExport name="YoYSchool" />
					<h3>By School District</h3>
					{generateChartsISD()}
				</div>
			</div>
		)
	}

	const generateChartsISD = () => {
		let data = chartDataISD()
		let opts = _.clone(lineOptions)
		opts.scales = {
			xAxes: [
				{
					//stacked: true,
				},
			],
			yAxes: [
				{
					//stacked: true,
				},
			],
		}
		opts.plugins = {
			datalabels: {},
		}
		//console.log(data)
		return (
			<div style={{ marginBottom: 70 }}>
				<Bar data={data} options={opts} />
			</div>
		)
	}

	const generateChartsMonth = () => {
		let data = chartDataMonth()
		let opts = _.clone(lineOptions)
		opts.scales = {
			xAxes: [
				{
					//stacked: true,
				},
			],
			yAxes: [
				{
					//stacked: true,
				},
			],
		}
		opts.plugins = {
			datalabels: {},
		}
		return (
			<div style={{ marginBottom: 70 }}>
				<Bar data={data} options={opts} />
			</div>
		)
	}

	const Months = () => {
		let rows = []
		let d = chartDataMonth()
		_.forEach(d.rawStore, (v, year) => {
			rows.push({
				Year: year,
				January: v.January,
				February: v.February,
				March: v.March,
				April: v.April,
				May: v.May,
				June: v.June,
				July: v.July,
				August: v.August,
				September: v.September,
				October: v.October,
				November: v.November,
				December: v.December,
				Total: v.total,
			})
		})
		console.log(rows)
		return (
			<>
				<h2>Month to Month</h2>
				<FullTable
					data={rows}
					columns={[
						{
							Header: "Year",
							accessor: "Year",
							filterMethod: (f, r) => helpers.tableText(f, r, "Year"),
						},
						{
							Header: "January",
							accessor: "January",
							filterMethod: (f, r) => helpers.tableText(f, r, "January"),
							Footer: (props) => helpers.tableTotals(props, "January", "0,0"),
						},
						{
							Header: "February",
							accessor: "February",
							filterMethod: (f, r) => helpers.tableText(f, r, "February"),
							Footer: (props) => helpers.tableTotals(props, "February", "0,0"),
						},
						{
							Header: "March",
							accessor: "March",
							filterMethod: (f, r) => helpers.tableText(f, r, "March"),
							Footer: (props) => helpers.tableTotals(props, "March", "0,0"),
						},
						{
							Header: "April",
							accessor: "April",
							filterMethod: (f, r) => helpers.tableText(f, r, "April"),
							Footer: (props) => helpers.tableTotals(props, "April", "0,0"),
						},
						{
							Header: "May",
							accessor: "May",
							filterMethod: (f, r) => helpers.tableText(f, r, "May"),
							Footer: (props) => helpers.tableTotals(props, "May", "0,0"),
						},
						{
							Header: "June",
							accessor: "June",
							filterMethod: (f, r) => helpers.tableText(f, r, "June"),
							Footer: (props) => helpers.tableTotals(props, "June", "0,0"),
						},
						{
							Header: "July",
							accessor: "July",
							filterMethod: (f, r) => helpers.tableText(f, r, "July"),
							Footer: (props) => helpers.tableTotals(props, "July", "0,0"),
						},
						{
							Header: "August",
							accessor: "August",
							filterMethod: (f, r) => helpers.tableText(f, r, "August"),
							Footer: (props) => helpers.tableTotals(props, "August", "0,0"),
						},
						{
							Header: "September",
							accessor: "September",
							filterMethod: (f, r) => helpers.tableText(f, r, "September"),
							Footer: (props) => helpers.tableTotals(props, "September", "0,0"),
						},
						{
							Header: "October",
							accessor: "October",
							filterMethod: (f, r) => helpers.tableText(f, r, "October"),
							Footer: (props) => helpers.tableTotals(props, "October", "0,0"),
						},
						{
							Header: "November",
							accessor: "November",
							filterMethod: (f, r) => helpers.tableText(f, r, "November"),
							Footer: (props) => helpers.tableTotals(props, "November", "0,0"),
						},
						{
							Header: "December",
							accessor: "December",
							filterMethod: (f, r) => helpers.tableText(f, r, "December"),
							Footer: (props) => helpers.tableTotals(props, "December", "0,0"),
						},
						{
							Header: "Total",
							accessor: "Total",
							filterMethod: (f, r) => helpers.tableText(f, r, "Total"),
							Footer: (props) => helpers.tableTotals(props, "Total", "0,0"),
						},
					]}
					name="yoy__byMonth"
					title="YoY Month"
					defaultSorted={[
						{
							id: "year",
							desc: true,
						},
					]}
				/>
			</>
		)
	}

	if (props.activeProperties.length !== 1) {
		return (
			<div className="pleaseChoose">
				<h1>Please choose one community to view this report.</h1>
			</div>
		)
	} else if (props.payloadStatus) {
		return (
			<>
				<div className="imageGroup">
					<Title title="Net Sales Year to Date, Year over Year" showDate={false} />
					<div className="switch content" style={{ marginBottom: 15 }} onClick={(e) => e.stopPropagation()}>
						<Switch
							onChange={() => setSeniorActive(!seniorActive)}
							checked={seniorActive}
							checkedIcon={false}
							uncheckedIcon={false}
							key={"switch"}
							id={"switch"}
						/>
						<p>Group Active Adult {seniorActive ? "On" : "Off"}</p>
					</div>
					<div className="content">{generateCharts()}</div>
					<div className="content">
						<Months />
					</div>
				</div>
			</>
		)
	} else {
		return ""
	}
}

function mapStateToProps(state) {
	return {
		currentDates: state.currentDates,
		compareDates: state.compareDates,
		payload: state.payload,
		payloadStatus: state.payloadStatus,
		activeProperties: state.activeProperties,
	}
}

function matchDispatchToProps(dispatch) {
	return bindActionCreators({}, dispatch)
}

export default connect(mapStateToProps, matchDispatchToProps)(YearOverYear)
